import useReportSort from '../../components/reports/useReportSort';
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiDownload,
  FiFileText,
  FiFilter,
  FiLoader,
  FiPackage,
  FiRefreshCw,
  FiSearch,
  FiShield,
} from "react-icons/fi";

import ReportFilterModal from "../../components/reports/ReportFilterModal";
import SearchableSelect from "../../components/reports/SearchableSelect";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";

const dateOnly = (value) => new Date(value).toISOString().slice(0, 10);
const firstDay = () => {
  const value = new Date();
  value.setDate(1);
  return dateOnly(value);
};

const initialFilters = () => ({
  from: firstDay(),
  to: dateOnly(new Date()),
  storeId: "",
  brand: "",
  group: "",
  search: "",
});

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const number = (value) =>
  Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

const blankQuantity = (value) => (Number(value || 0) ? number(value) : "");
const blankMoney = (value) => (Number(value || 0) ? money(value) : "");

const unique = (rows, key) =>
  [...new Set(rows.map((row) => row[key]).filter(Boolean))].sort((a, b) =>
    String(a).localeCompare(String(b)),
  );

function downloadFile(blob, name) {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

function Field({ label, children }) {
  return (
    <label className="block text-sm font-semibold text-slate-700">
      <span className="mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

export default function ProductWiseSaleAndStockReportPage() {
  const { accessToken, user } = useAuth();
  const canGenerate = hasPermission(
    user,
    "reports.product_wise_sale_stock.generate",
  );
  const canExport = hasPermission(
    user,
    "reports.product_wise_sale_stock.export",
  );
  const [stores, setStores] = useState([]);
  const [rawReport, setReport] = useState(null);
  const { report, controls: sortControls } = useReportSort(rawReport);
  const [showFilters, setShowFilters] = useState(false);
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const loadStores = useCallback(async () => {
    try {
      const response = await apiRequest(
        "/business-reports/product-wise-sale-stock/stores",
        { accessToken },
      );
      setStores(response || []);
    } catch (requestError) {
      setError(requestError.message || "Could not load stores.");
    }
  }, [accessToken]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const rows = report?.rows || [];
  const brands = useMemo(() => unique(rows, "companyName"), [rows]);
  const groups = useMemo(() => unique(rows, "groupName"), [rows]);

  const visibleRows = useMemo(() => {
    const search = appliedFilters.search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesSearch =
        !search ||
        [
          row.itemName,
          row.itemCode,
          row.sku,
          row.productCode,
          row.lotCode,
          row.shadeName,
          row.godownName,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(search));

      return (
        (!appliedFilters.brand || row.companyName === appliedFilters.brand) &&
        (!appliedFilters.group || row.groupName === appliedFilters.group) &&
        matchesSearch
      );
    });
  }, [appliedFilters, rows]);

  const summary = useMemo(
    () =>
      visibleRows.reduce(
        (total, row) => ({
          lots: total.lots + 1,
          purchase: total.purchase + Number(row.purchase || 0),
          purchaseValue: total.purchaseValue + Number(row.purchaseValue || 0),
          netSale: total.netSale + Number(row.netSale || 0),
          saleValue: total.saleValue + Number(row.saleValue || 0),
          closingStock: total.closingStock + Number(row.closingStock || 0),
          closingValue: total.closingValue + Number(row.closingValue || 0),
        }),
        {
          lots: 0,
          purchase: 0,
          purchaseValue: 0,
          netSale: 0,
          saleValue: 0,
          closingStock: 0,
          closingValue: 0,
        },
      ),
    [visibleRows],
  );

  const updateFilter = (key, value) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  };

  const generate = async () => {
    if (!canGenerate) return;
    setLoading(true);
    setError("");

    try {
      const nextReport = await apiRequest(
        "/business-reports/product-wise-sale-stock/generate",
        {
          method: "POST",
          body: {
            from: draftFilters.from,
            to: draftFilters.to,
            ...(draftFilters.storeId ? { storeId: draftFilters.storeId } : {}),
          },
          accessToken,
        },
      );
      setReport(nextReport);
      setAppliedFilters({ ...draftFilters });
      setShowFilters(false);
    } catch (requestError) {
      setReport(null);
      setError(requestError.message || "Could not generate this report.");
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    if (!report || !canExport) return;
    setExporting(true);
    setError("");

    try {
      const params = new URLSearchParams({
        from: appliedFilters.from,
        to: appliedFilters.to,
        format: "XLSX",
      });
      if (appliedFilters.storeId) params.set("storeId", appliedFilters.storeId);
      const response = await apiRequest(
        `/business-reports/product-wise-sale-stock/export?${params.toString()}`,
        { accessToken, rawResponse: true },
      );
      const name =
        response.headers
          .get("content-disposition")
          ?.match(/filename="?([^";]+)"?/)?.[1] ||
        "charms-product-wise-sale-and-stock.xlsx";
      downloadFile(await response.blob(), name);
    } catch (requestError) {
      setError(requestError.message || "Could not export this report.");
    } finally {
      setExporting(false);
    }
  };

  const openFilters = () => {
    setDraftFilters({ ...appliedFilters });
    setShowFilters(true);
  };

  const cancelFilters = () => {
    setDraftFilters({ ...appliedFilters });
    setShowFilters(false);
  };

  const columns = [
    "Company name",
    "Item name",
    "Group name",
    "Pack / size",
    "Purchase",
    "Purchase value-1",
    "Net sale-1",
    "Sale value-1",
    "Closing stock",
    "Closing value (pur rate)",
    "Lots",
    "Shade name",
    "Godown name",
    "Item code",
  ];

  return (
    <div className="business-report space-y-5">
      <section className="border-b border-slate-200 bg-white px-1 pb-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[.18em] text-primary-800">
              <FiPackage /> Business reports
            </div>
            <h1 className="text-3xl font-bold text-slate-950">
              Product Wise Sale and Stock
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-500">
              Period-wise GRN purchase, net sale and current closing stock by
              product lot.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openFilters}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-primary-300 hover:text-primary-800"
            >
              <FiFilter /> Filters
            </button>
            <button
              type="button"
              onClick={generate}
              disabled={!canGenerate || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiRefreshCw />
              )}
              {loading ? "Generating…" : "Generate"}
            </button>
            <button
              type="button"
              onClick={exportExcel}
              disabled={!report || !canExport || exporting}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-primary-300 hover:text-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {exporting ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiDownload />
              )}
              Export Excel
            </button>
          </div>
        </div>
      </section>

      {error && (
        <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {!canGenerate && (
        <p className="flex gap-2 rounded-lg bg-amber-50 p-4 text-sm text-amber-800">
          <FiShield className="mt-0.5 shrink-0" />
          You do not have permission to generate this report.
        </p>
      )}

      {!report ? (
        <section className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-20 text-center">
          <FiFileText className="mx-auto mb-4 text-4xl text-primary-300" />
          <h2 className="text-xl font-bold text-slate-900">
            No report generated yet
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Choose a period and optional filters, then select Generate.
          </p>
        </section>
      ) : (
        <>
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[
              ["Lots", number(summary.lots), "text-primary-800"],
              ["Purchase value", money(summary.purchaseValue), "text-sky-700"],
              ["Sale value", money(summary.saleValue), "text-emerald-700"],
              ["Closing value", money(summary.closingValue), "text-amber-700"],
            ].map(([label, value, color]) => (
              <div
                key={label}
                className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <p className="text-sm text-slate-500">{label}</p>
                <p className={`mt-2 text-2xl font-bold ${color}`}>{value}</p>
              </div>
            ))}
          </section>

          <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-2 border-b border-slate-100 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-950">
                  Product-wise sale and stock
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {visibleRows.length.toLocaleString("en-IN")} product
                  variant(s). Purchase and sale columns only include activity in
                  the selected period.
                </p>
              </div>
              <p className="text-xs font-medium text-slate-500">
                {appliedFilters.from} to {appliedFilters.to}
              </p>
            </div>
            <div className="overflow-x-auto">
                  {sortControls}
              <table className="min-w-[1850px] w-full text-sm">
                <thead className="bg-slate-100 text-left text-xs font-bold uppercase tracking-wide text-slate-600">
                  <tr>
                    {columns.map((header) => (
                      <th
                        key={header}
                        className="whitespace-nowrap border-b border-slate-200 px-4 py-3"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr
                      key={`${row.itemCode}-${row.packSize}`}
                      className="border-b border-slate-100 text-slate-700 hover:bg-primary-50/30"
                    >
                      <td className="px-4 py-3">{row.companyName || "—"}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900">
                        {row.itemName}
                        <p className="mt-1 text-xs font-normal text-slate-500">
                          {row.productCode || "—"}
                        </p>
                      </td>
                      <td className="px-4 py-3">{row.groupName || "—"}</td>
                      <td className="px-4 py-3">{row.packSize || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        {blankQuantity(row.purchase)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {blankMoney(row.purchaseValue)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {blankQuantity(row.netSale)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {blankMoney(row.saleValue)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        {blankQuantity(row.closingStock)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        {blankMoney(row.closingValue)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        {number(row.lotCount || 1)}
                      </td>
                      <td className="px-4 py-3">{row.shadeName || "—"}</td>
                      <td className="px-4 py-3">{row.godownName}</td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {row.itemCode}
                      </td>
                    </tr>
                  ))}
                  {!visibleRows.length && (
                    <tr>
                      <td
                        colSpan="14"
                        className="px-4 py-16 text-center text-sm text-slate-500"
                      >
                        No products match the generated filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      <ReportFilterModal
        open={showFilters}
        title="Product wise sale and stock filters"
        description="Choose the report period and optional filters. Generate applies the filters and closes this window."
        loading={loading}
        onApply={generate}
        onClose={cancelFilters}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="From date">
            <input
              type="date"
              value={draftFilters.from}
              onChange={(event) => updateFilter("from", event.target.value)}
              className="report-input"
            />
          </Field>
          <Field label="To date">
            <input
              type="date"
              value={draftFilters.to}
              onChange={(event) => updateFilter("to", event.target.value)}
              className="report-input"
            />
          </Field>
          <Field label="Godown / Store">
            <SearchableSelect
              value={draftFilters.storeId}
              onChange={(value) => updateFilter("storeId", value)}
              placeholder="All stores"
              options={stores.map((store) => ({
                value: store.id,
                label: `${store.name} (${store.code})`,
              }))}
            />
          </Field>
          <Field label="Company / Brand">
            <SearchableSelect
              value={draftFilters.brand}
              onChange={(value) => updateFilter("brand", value)}
              placeholder="All brands"
              options={brands}
            />
          </Field>
          <Field label="Item group">
            <SearchableSelect
              value={draftFilters.group}
              onChange={(value) => updateFilter("group", value)}
              placeholder="All item groups"
              options={groups}
            />
          </Field>
          <Field label="Search product, barcode or lot">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={draftFilters.search}
                onChange={(event) => updateFilter("search", event.target.value)}
                className="report-input pl-9"
                placeholder="Product, item code or lot code"
              />
            </div>
          </Field>
        </div>
      </ReportFilterModal>
    </div>
  );
}
